/**
 * SteemWorld Feeds API
 *
 * Provides methods for fetching feed data from the SteemWorld feeds_api.
 */

import { feedsApi } from '../client'
import { STEEMWORLD_CONFIG } from '../config'
import type { FeedsApiResponse, FeedsQueryParams } from '../types'

/**
 * Builds common feed query parameters
 */
function buildFeedParams(params: FeedsQueryParams = {}) {
  const { defaults } = STEEMWORLD_CONFIG

  return {
    limit: params.limit ?? defaults.limit,
    offset: params.offset ?? defaults.offset,
    bodyLength: params.bodyLength ?? defaults.bodyLength,
    observer: params.observer,
  }
}

/**
 * Fetches feed for a user's followed accounts
 *
 * @param account - The account username
 * @param params - Query parameters
 * @returns Feed data from followed accounts
 */
export async function getAccountFriendsFeed(
  account: string,
  params: FeedsQueryParams = {}
): Promise<FeedsApiResponse> {
  return feedsApi.request<FeedsApiResponse>(`/getAccountFriendsFeed/${account}`, {
    params: buildFeedParams(params),
  })
}

/**
 * Fetches all posts by a specific author
 *
 * @param account - The account username
 * @param params - Query parameters
 * @returns All posts by the author
 */
export async function getAccountPosts(
  account: string,
  params: FeedsQueryParams = {}
): Promise<FeedsApiResponse> {
  return feedsApi.request<FeedsApiResponse>(`/getAccountPosts/${account}`, {
    params: buildFeedParams(params),
  })
}

/**
 * Fetches account blog (user's own posts + resteemed posts)
 *
 * @param account - The account username
 * @param params - Query parameters
 * @returns Blog posts including resteems
 */
export async function getAccountBlog(
  account: string,
  params: FeedsQueryParams = {}
): Promise<FeedsApiResponse> {
  return feedsApi.request<FeedsApiResponse>(`/getAccountBlog/${account}`, {
    params: buildFeedParams(params),
  })
}

/**
 * Fetches posts written by a specific author (excluding resteems)
 *
 * @param author - The author username
 * @param params - Query parameters
 * @returns Only original posts, no resteems
 */
export async function getPostsByAuthor(
  author: string,
  params: FeedsQueryParams = {}
): Promise<FeedsApiResponse> {
  return feedsApi.request<FeedsApiResponse>(`/getPostsByAuthor/${author}`, {
    params: buildFeedParams(params),
  })
}

/**
 * Feeds API namespace
 */
export const feeds = {
  getAccountFriendsFeed,
  getAccountPosts,
  getAccountBlog,
  getPostsByAuthor,
}
